import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EMPTY_FILTER_VALUE } from '@sib/api/constants';

export const MAX_NUMBER_RANGE = 1000000;

interface UseNumberRangeFilterOptions {
  unit?: string;
  placeholder?: string;
  onChange?: (from: string, to: string) => void;
  onClear?: () => void;
}

export function useNumberRangeFilter(
  value: string[],
  min: number,
  max: number,
  options: UseNumberRangeFilterOptions = {}
) {
  const [open, setOpen] = useState(false);

  const { unit, placeholder, onChange, onClear } = options;

  const isEmptyFilter = value.length === 1 && value[0] === EMPTY_FILTER_VALUE;

  const fromValue = isEmptyFilter || !value[0] ? min : Number(value[0]);
  const toValue = isEmptyFilter || !value[1] ? max : Number(value[1]);

  const [from, setFrom] = useState<number | ''>(fromValue);
  const [to, setTo] = useState<number | ''>(toValue);

  const clamp = useCallback(
    (n: number) => Math.min(max, Math.max(min, n)),
    [min, max]
  );

  useEffect(() => {
    if (!open) return;
    const appliedFrom = value[0] ? Number(value[0]) : min;
    const appliedTo = value[1] ? Number(value[1]) : max;
    setFrom(clamp(appliedFrom));
    setTo(clamp(appliedTo));
  }, [open, min, max, value, clamp]);

  const handleSliderChange = useCallback(
    (range: number[]) => {
      const [a, b] = range;
      const low = clamp(Math.min(a, b));
      const high = clamp(Math.max(a, b));
      setFrom(low);
      setTo(high);
    },
    [clamp]
  );

  const handleFromInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setFrom('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setFrom(v);
      if (to !== '' && v > to) setTo(v);
    },
    [clamp, to]
  );

  const handleToInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') return setTo('');

      const num = Number(raw);
      if (!Number.isFinite(num)) return;
      const v = clamp(num);
      setTo(v);
      if (from !== '' && v < from) setFrom(v);
    },
    [clamp, from]
  );

  const handleClear = useCallback(() => {
    setFrom(min);
    setTo(max);
    onClear?.();
  }, [min, max, onClear]);

  const formatWithUnit = useCallback(
    (value: number) => (unit ? `${value} ${unit}` : String(value)),
    [unit]
  );

  const handleConfirm = useCallback(() => {
    const fromNum = from === '' ? min : clamp(from);
    const toNum = to === '' ? max : clamp(to);
    onChange?.(String(fromNum), String(toNum));
    setOpen(false);
  }, [from, to, min, max, clamp, onChange]);

  const hasAppliedRange = value.length >= 2 && value[0] !== EMPTY_FILTER_VALUE;
  const isDefaultRange =
    (from === '' || from === min) && (to === '' || to === max);
  const buttonLabel = useMemo(
    () =>
      hasAppliedRange
        ? `${formatWithUnit(fromValue)} - ${formatWithUnit(toValue)}`
        : (placeholder ?? null),
    [hasAppliedRange, fromValue, toValue, placeholder, formatWithUnit]
  );
  const sliderValue = [from === '' ? min : from, to === '' ? max : to];

  return {
    from,
    to,
    fromValue,
    toValue,
    hasAppliedRange,
    isDefaultRange,
    buttonLabel,
    sliderValue,
    open,
    handleSliderChange,
    handleFromInputChange,
    handleToInputChange,
    handleClear,
    handleConfirm,
    formatWithUnit,
    setOpen,
  };
}
